/**
 * StatCard Component
 * Reusable stats/metric card for dashboards
 */

import { html, attr, When, type Renderable, Value, Ensure } from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon } from '@tempots/beatui'

// ============================================================================
// Types
// ============================================================================

export type StatCardColor = 'green' | 'blue' | 'purple' | 'gray' | 'brand'

export interface StatCardOptions {
  /** Card title/label */
  title: string
  /** Main value to display */
  value: Value<string>
  /** Optional suffix for the value (e.g., "GB", "credits") */
  valueSuffix?: Value<string>
  /** Optional subtitle below the value */
  subtitle?: Value<string | undefined>
  /** Icon name (mdi format) */
  icon: string
  /** Color theme for the icon */
  color?: StatCardColor
  /** Optional link URL */
  link?: Value<{ url: string; text: string } | null>
}

// ============================================================================
// Color Classes
// ============================================================================

const colorClasses: Record<StatCardColor, string> = {
  green: 'bg-green-100 text-green-600',
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  gray: 'bg-gray-100 text-gray-600',
  brand: 'bg-brand-100 text-brand-600',
}

// ============================================================================
// Component
// ============================================================================

export function StatCard({
  title,
  value,
  valueSuffix,
  subtitle,
  icon,
  color = 'gray',
  link,
}: StatCardOptions): Renderable {
  const valueSignal = Value.toSignal(value)
  const subtitleSignal = subtitle ? Value.toSignal(subtitle) : undefined

  return html.div(
    attr.class('bg-white rounded-xl border border-gray-200 p-4 sm:p-6'),
    // Header with title and icon
    html.div(
      attr.class('flex items-center justify-between mb-4'),
      html.h3(attr.class('text-sm font-medium text-gray-500'), title),
      html.div(
        attr.class(
          `w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`
        ),
        Icon({ icon }, attr.class('w-5 h-5'))
      )
    ),
    // Value
    html.p(
      attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
      valueSignal,
      When(valueSuffix !== undefined, () =>
        html.span(
          attr.class('text-base sm:text-lg font-medium text-gray-500 ml-2'),
          valueSuffix!
        )
      )
    ),
    // Subtitle (optional)
    subtitleSignal
      ? Ensure(
          subtitleSignal,
          (subtitle) =>
            html.p(attr.class('text-sm text-gray-500 mt-1 mb-4'), subtitle),
          () => (link ? html.div(attr.class('mb-4')) : null)
        )
      : link
        ? html.div(attr.class('mb-4'))
        : null,
    // Link (optional)
    Ensure(link, (link) =>
      Anchor(
        { href: link.$.url },
        attr.class(
          'inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700'
        ),
        link.$.text,
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-4 h-4 ml-1'))
      )
    )
  )
}
